import axios, { CancelToken } from 'axios';
import { BASE_URL, API } from '@mdc/constants';
import { logService } from '@mdc/services';

class BackendService {
    constructor() {
        this.baseUrl = BASE_URL + API.backend.baseUrl;

        this.instance = axios.create({
            baseURL: this.baseUrl,
            timeout: API.backend.defaultTimeout
        });
    }

    addCommonHeader(headerObject) {
        Object.entries(headerObject).forEach(([key, value]) => {
            this.instance.defaults.headers.common[key] = value;
        });
    }

    getCommonHeaders() {
        return this.instance.defaults.headers.common;
    }

    /**
     * @example
     *
     * const [aak, cancelAak] = BackendService.addApiKey();
     * await aak;
     * // OR
     * aak.then(...)
     *
     * // cancel the request
     * cancelAak();
     *
     */
    addApiKey = (apikey, data, headers) => {
        let cancel;
        const promise = this.instance.post(`/token/${apikey}`, data, {
            headers: Object.assign({}, this.getCommonHeaders(), headers),
            cancelToken: new CancelToken(function executor(c) {
                cancel = c;
            })
        });

        return [promise, cancel];
    }

    updateApiKey = (data, headers) => {
        let cancel;
        const promise = this.instance.put('/token', data, {
            headers: Object.assign({}, this.getCommonHeaders(), headers),
            cancelToken: new CancelToken(function executor(c) {
                cancel = c;
            })
        });

        return [promise, cancel];
    }

    getGuestSession = (data, headers) => {
        let cancel;
        const promise = this.instance.post('/token/get-guest-session', data, {
            headers: Object.assign({}, this.getCommonHeaders(), headers),
            cancelToken: new CancelToken(function executor(c) {
                cancel = c;
            })
        });

        return [promise, cancel];
    }

    transferReputation = (data, headers) => {
        let cancel;
        const promise = this.instance.post('/token/transfer-reputation', data, {
            headers: Object.assign({}, this.getCommonHeaders(), headers),
            cancelToken: new CancelToken(function executor(c) {
                cancel = c;
            })
        });

        return [promise, cancel];
    }

    feedback = (data, headers) => {
        let cancel;
        const promise = this.instance.post('/feedback', data, {
            headers: Object.assign({}, this.getCommonHeaders(), headers),
            cancelToken: new CancelToken(function executor(c) {
                cancel = c;
            })
        });

        return [promise, cancel];
    }

    /**
     * Create a subscription payment on stripe
     *
     * @param {[string]} paymentPlans the Stripe payment plans IDs
     * @returns {Promise<[data, cancel]>} [data, cancel]
     * @this axios error
     */
    createSubscription = async (paymentPlans, currency, coupon, selectedPlansPrices) => {
        const url = '/store/create-subscription';
        let couponHeader = {};
        if (coupon) {
            couponHeader = { coupon };
        }

        const headers = Object.assign({
            paymentPlans: JSON.stringify(paymentPlans),
            currency,
            selectedPlansPrices: JSON.stringify(selectedPlansPrices)
        }, couponHeader, this.getCommonHeaders());

        const { data } = await this.instance.get(url, { headers });

        return [data];
    }

    /**
     * Get store coupon details
     * @param   {string}    couponName  Coupon Name
     * @returns {Promise<[data, cancel]>}   [data, cancel]
     */
    getCouponDetails = async (couponName) => {
        let cancel;
        if (!couponName) {
            // eslint-disable-next-line no-console
            logService.log('Missing coupon name');
            return;
        }
        const url = `/store/coupon/${couponName}`;

        const promise = await this.instance.get(url, {
            cancelToken: new CancelToken(function executor(c) {
                cancel = c;
            })
        });

        return [promise, cancel];
    }

    /**
     * Validate email for store eligibility
     *
     * @this axios error
     */
    emailValidator = async () => {
        let data = {};
        try {
            const url = '/store/email-validator';
            const headers = Object.assign({}, this.getCommonHeaders());
            data = await this.instance.get(url, { headers });
        } catch (error) {
            if (error?.response?.data?.code === 400032)
            {
                data.status = 'Not eligible';
            }
        }
        return data;
    }

    /**
     *
     * @param {string} currency
     */
    createBillingPortalSession = async (currency) => {
        let data = {};
        try {
            const url = '/store/create-portal-session';
            const headers = Object.assign({ currency }, this.getCommonHeaders());
            data = await this.instance.get(url, { headers });
        } catch (error) {
            data.error = error;
        }

        return data;
    }

    /**
     * Get ip based geo-location info
     *
     * @returns {Promise<*>} `{continentCode, countryCode, cityCode, city}`
     * @throws axios error
     */
    getGeoLocation = async() => {
        const url = '/utils/location';
        const headers = Object.assign({}, this.getCommonHeaders());
        const { data } = await this.instance.get(url, { headers });
        return data;
    }

    getMitreData = async (ttp) => {
        let data = {};
        // eslint-disable-next-line no-useless-catch
        try {
            const url = '/results/mitre-data';
            const headers = Object.assign({ ttp }, this.getCommonHeaders());
            data = await this.instance.get(url, { headers });

            return data;
        } catch (error) {
            throw error;
        }

    }

    /**
     * @param {object} data post body
     * @param {string} route
     */
    postRequest = (data, route) => {
        let cancel;
        const promise = this.instance.post(route, data, {
            headers: Object.assign({}, this.getCommonHeaders()),
            cancelToken: new CancelToken(function executor(c) {
                cancel = c;
            })
        });

        return [promise, cancel];
    };

    /**
     * Get [dashboard, statistics] reports
     *
     * @param {object} data post body with user info and hours range
     * @param {string} data.customerId - 'sso_user_id' from GET apikey API
     * @param {string} [data.organizationId] - (optional) 'organization.organization_id' from GET apikey API if available
     * @param {number} data.hours - hours range
     * @param {string} reportName - ['dashboard', 'statistics']
     */
    postReports = (data, reportName) => this.postRequest(data, '/reports/' + reportName);

    /**
     * Check if user is admin of organization or not
     *
     * @param {object} data post body with user info and hours range
     * @param {string} data.userId - 'sso_user_id' from GET apikey API
     * @param {string} data.organizationId - 'organization.organization_id' from GET apikey API if available
     */
    postIsOrganizationAdmin = (data) => this.postRequest(data, '/organization/is-admin');

    /**
     * Get ip whitelist on organization
     *
     * @param {object} data post body with user info and hours range
     * @param {string} data.userId - 'sso_user_id' from GET apikey API
     * @param {string} data.organizationId - 'organization.organization_id' from GET apikey API if available
     */
    postGetOrganizationIpWhitelist = (data) => this.postRequest(data, '/organization/get-ips');

    /**
     * Update ip whitelist on organization
     *
     * @param {object} data post body with user info and hours range
     * @param {string} data.userId - 'sso_user_id' from GET apikey API
     * @param {string} data.organizationId - 'organization.organization_id' from GET apikey API if available
     * @param {string[]} data.ipWhitelist - IP whitelist for organization
     */
    postUpdateOrganizationIpWhitelist = (data) => this.postRequest(data, '/organization/update-ips');
}

export default new BackendService();
